import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useMessages } from "../context/MessagesContext";
import * as api from "../api";

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days === 1) return "Hier";
  return date.toLocaleDateString("fr-DZ", { day: "2-digit", month: "short" });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString("fr-DZ", { hour: "2-digit", minute: "2-digit" });
}

function Messages() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    conversations,
    activeMessages,
    sendMessage,
    unread,
    fetchConversations,
    setActiveConvId,
  } = useMessages();

  const [selectedConvId, setSelectedConvId] = useState(convId ? parseInt(convId) : null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user) navigate("/connexion");
  }, [user, navigate]);

  // ✅ CORRECTION : sélectionner la conversation depuis l'URL une fois les conversations chargées
  useEffect(() => {
    if (convId && conversations.length > 0) {
      const id = parseInt(convId);
      const conv = conversations.find(c => c.id === id);
      if (conv) {
        setSelectedConvId(id);
        setActiveConvId(id);
      }
    }
  }, [convId, conversations]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (selectedConvId !== null && selectedConvId !== undefined) {
      setActiveConvId(selectedConvId);
    } else {
      setActiveConvId(null);
    }
  }, [selectedConvId]);

 useEffect(() => {
  if (activeMessages && selectedConvId !== null) {
    const filtered = activeMessages.filter(m => Number(m.conversation_id) === Number(selectedConvId));
    setSelectedMessages(prev => {
      const serverIds = new Set(filtered.map(m => String(m.id)));
      const optimistes = prev.filter(m => m._optimiste && !serverIds.has(String(m.id)));
      return [...filtered, ...optimistes];
    });
  }
}, [activeMessages, selectedConvId]);
  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (selectedMessages.length > prevMsgCount.current) {
      scrollToBottom();
    }
    prevMsgCount.current = selectedMessages.length;
  }, [selectedMessages.length]);

  if (!user) return null;

  const filteredConvs = conversations.filter(c =>
    (c.annonce_nom || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.participants || []).some(p =>
      `${p.prenom} ${p.nom}`.toLowerCase().includes(search.toLowerCase())
    )
  );

  const selectedConv = conversations.find(c => c.id === selectedConvId);

  const getOtherParticipant = (conv) => {
    if (!conv || !conv.participants) return null;
    return conv.participants.find(p => p.id !== user.id);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || selectedConvId === null || !selectedConv || sending) return;
    setSending(true);
    const texte = newMessage.trim();
    setNewMessage("");

    const msgOptimiste = {
      id: Date.now(),
      conversation_id: selectedConvId,
      sender_id: user.id,
      sender_nom: `${user.prenom} ${user.nom}`,
      texte,
      created_at: new Date().toISOString(),
      lu: 0,
      _optimiste: true,
    };
    setSelectedMessages(prev => [...prev, msgOptimiste]);
    setTimeout(() => scrollToBottom(), 100);

    try {
      const other = getOtherParticipant(selectedConv);
      if (!other) { setSending(false); return; }

      await sendMessage({
        receiverId: other.id,
        annonceId: selectedConv.annonce_id,
        annonceNom: selectedConv.annonce_nom,
        annonceImage: selectedConv.annonce_image || "",
        texte,
      });
      const r = await api.getMessages(selectedConvId);
      setSelectedMessages([...r.data]);

      await fetchConversations();
    } catch (err) {
      console.error(err);
      setSelectedMessages(prev => prev.filter(m => m.id !== msgOptimiste.id));
      setNewMessage(texte);
    } finally {
      setSending(false);
    }
  };

  const handleSelectConv = (id) => {
    setSelectedConvId(id);
    setSelectedMessages([]);
    setActiveConvId(id);
  };

  const getRoleColor = (role) => {
    if (role === "exposant") return "#0d9488";
    if (role === "admin") return "#dc2626";
    return "#6366f1";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />
      <div style={{ flex: 1, backgroundColor: "#f9fafb", padding: isMobile ? "16px" : "24px 40px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

          <div style={{ marginBottom: "16px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: "12px" }}>
            <div>
              <h1 style={{ fontSize: "26px", fontWeight: "700", color: "#1f2937" }}>
                Messagerie
                {unread > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#dc2626", color: "white", borderRadius: "20px", fontSize: "12px", fontWeight: "700", padding: "2px 8px", marginLeft: "10px", verticalAlign: "middle" }}>
                    {unread}
                  </span>
                )}
              </h1>
              <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}>
                {conversations.length} conversation{conversations.length > 1 ? "s" : ""}
              </p>
            </div>
            <Link to="/catalogue" style={{ display: "inline-flex", alignItems: "center", gap: "8px", backgroundColor: "#0d9488", color: "white", padding: "10px 18px", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "600", whiteSpace: "nowrap" }}>
              Parcourir le catalogue
            </Link>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "340px 1fr", backgroundColor: "white", borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden", height: isMobile ? "calc(100vh - 160px)" : "calc(100vh - 220px)" }}>

            {/* LEFT: Liste conversations */}
            <div style={{ borderRight: isMobile ? "none" : "1px solid #f3f4f6", display: isMobile && selectedConvId ? "none" : "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
              <div style={{ padding: "16px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#f9fafb", borderRadius: "8px", padding: "8px 12px", border: "1px solid #e5e7eb" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ border: "none", outline: "none", fontSize: "13px", backgroundColor: "transparent", flex: 1 }}
                  />
                </div>
              </div>

              <div style={{ flex: 1, overflowY: "auto" }}>
                {filteredConvs.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <p style={{ color: "#9ca3af", fontSize: "14px" }}>Aucune conversation.</p>
                  </div>
                ) : (
                  filteredConvs.map(conv => {
                    const isSelected = selectedConvId === conv.id;
                    const other = getOtherParticipant(conv);
                    return (
                      <div key={conv.id} onClick={() => handleSelectConv(conv.id)}
                        style={{ display: "flex", gap: "12px", alignItems: "flex-start", padding: "14px 16px", cursor: "pointer", backgroundColor: isSelected ? "#f0fdfa" : "white", borderBottom: "1px solid #f9fafb", borderLeft: isSelected ? "3px solid #0d9488" : "3px solid transparent" }}>
                        <div style={{ width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0, backgroundColor: getRoleColor(other?.role || "acheteur"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", color: "white" }}>
                          {(other?.prenom?.[0] || "")}{(other?.nom?.[0] || "")}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                            <span style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {other?.prenom} {other?.nom}
                            </span>
                            {conv.last_message_date && (
                              <span style={{ fontSize: "11px", color: "#9ca3af", flexShrink: 0, marginLeft: "8px" }}>
                                {formatDate(conv.last_message_date)}
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#0d9488", fontWeight: "600", marginBottom: "4px" }}>
                            {conv.annonce_image && (
                              <img src={conv.annonce_image} alt={conv.annonce_nom}
                                style={{ width: "16px", height: "16px", borderRadius: "3px", objectFit: "cover", flexShrink: 0 }}
                                onError={e => { e.target.style.display = "none"; }}
                              />
                            )}
                            📦 {conv.annonce_nom}
                          </div>
                          <div style={{ fontSize: "12px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {conv.last_message || "Démarrer la conversation..."}
                          </div>
                          {conv.unread_count > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#dc2626", color: "white", borderRadius: "20px", fontSize: "10px", fontWeight: "700", padding: "1px 6px", marginTop: "4px" }}>
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* RIGHT: Conversation */}
            <div style={{ display: isMobile && !selectedConvId ? "none" : "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
              {selectedConv ? (
                <>
                  {(() => {
                    const other = getOtherParticipant(selectedConv);
                    return (
                      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: "14px", backgroundColor: "white", flexShrink: 0 }}>
                        {isMobile && (
                          <button onClick={() => setSelectedConvId(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex", alignItems: "center", color: "#6b7280", flexShrink: 0 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                          </button>
                        )}
                        <div style={{ width: "42px", height: "42px", borderRadius: "50%", backgroundColor: getRoleColor(other?.role || "acheteur"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", color: "white", flexShrink: 0 }}>
                          {(other?.prenom?.[0] || "")}{(other?.nom?.[0] || "")}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937" }}>{other?.prenom} {other?.nom}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "2px" }}>
                            {selectedConv.annonce_image && (
                              <img src={selectedConv.annonce_image} alt={selectedConv.annonce_nom}
                                style={{ width: "20px", height: "20px", borderRadius: "4px", objectFit: "cover", flexShrink: 0 }}
                                onError={e => { e.target.style.display = "none"; }}
                              />
                            )}
                            <div style={{ fontSize: "12px", color: "#9ca3af" }}>{selectedConv.annonce_nom}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#9ca3af" }}>
                          <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#22c55e" }} />
                          En direct
                        </div>
                      </div>
                    );
                  })()}

                  <div ref={messagesContainerRef}
                    style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "16px", backgroundColor: "#fafafa" }}>
                    {selectedMessages.length === 0 ? (
                      <div style={{ textAlign: "center", margin: "auto" }}>
                        <p style={{ color: "#9ca3af", fontSize: "14px" }}>Démarrez la conversation !</p>
                      </div>
                    ) : (
                      selectedMessages.map((msg, index) => {
                        const isMe = msg.sender_id === user.id;
                        const other = getOtherParticipant(selectedConv);
                        return (
                          <div key={msg.id || index} style={{ display: "flex", flexDirection: isMe ? "row-reverse" : "row", alignItems: "flex-end", gap: "8px" }}>
                            {!isMe && (
                              <div style={{ width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0, backgroundColor: "#0d9488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: "white" }}>
                                {(other?.prenom?.[0] || "")}
                              </div>
                            )}
                            <div style={{ maxWidth: "65%" }}>
                              <div style={{ backgroundColor: isMe ? "#0d9488" : "white", color: isMe ? "white" : "#1f2937", padding: "10px 14px", borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px", fontSize: "14px", lineHeight: "1.5", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", opacity: msg._optimiste ? 0.7 : 1 }}>
                                {msg.texte}
                              </div>
                              <div style={{ fontSize: "10px", color: "#9ca3af", textAlign: isMe ? "right" : "left", marginTop: "4px" }}>
                                {msg._optimiste ? "Envoi..." : formatTime(msg.created_at)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={{ padding: "16px", borderTop: "1px solid #f3f4f6", backgroundColor: "white", flexShrink: 0 }}>
                    <form onSubmit={handleSend} style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                      <div style={{ flex: 1, backgroundColor: "#f9fafb", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "10px 14px" }}>
                        <textarea
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                          placeholder="Écrivez votre message..."
                          rows={1}
                          style={{ flex: 1, border: "none", outline: "none", fontSize: "14px", backgroundColor: "transparent", resize: "none", fontFamily: "inherit", lineHeight: "1.5", width: "100%" }}
                        />
                      </div>
                      <button type="submit" disabled={!newMessage.trim() || sending}
                        style={{ width: "44px", height: "44px", borderRadius: "12px", backgroundColor: newMessage.trim() && !sending ? "#0d9488" : "#e5e7eb", color: newMessage.trim() && !sending ? "white" : "#9ca3af", border: "none", cursor: newMessage.trim() && !sending ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "16px", backgroundColor: "#fafafa", flex: 1 }}>
                  <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <h3 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", marginBottom: "8px" }}>Sélectionnez une conversation</h3>
                    <p style={{ fontSize: "14px", color: "#9ca3af", maxWidth: "280px", lineHeight: "1.5" }}>Choisissez une conversation à gauche ou contactez un exposant depuis une annonce.</p>
                  </div>
                  <Link to="/catalogue" style={{ marginTop: "8px", padding: "10px 20px", backgroundColor: "#0d9488", color: "white", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "600" }}>
                    Parcourir le catalogue
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Messages;